import type { PrioridadeTriagem } from '../../didatico/ocupantes'
import type { BlocoVitimaTabletop, CenaTabletop, ObjetoTabletop } from '../tipos'
import { acionamentoDaCena, mensagemAcionamento } from './acionamento'
import { SLOTS_MONITOR, leituraCurta } from './catalogoAcoes'
import { ESTADO_VITIMA_VAZIO, problemasVisiveisAtivos, ultimaLeitura } from './efeitosClinicos'
import { aparenciasDosRiscos, type AparenciaRisco } from './riscos'
import type {
  FaseAtendimento,
  FichaVitimaAtendimento,
  MarcoOcorrencia,
  SessaoAtendimentoTabletop,
} from './tipos'

/**
 * O que a janela de projeção (2ª tela / sala de aula) pode receber.
 *
 * Montado campo a campo a partir de uma lista explícita — nada de copiar a
 * cena e a sessão inteiras e esconder o resto na interface. Ficam de fora:
 * a triagem de referência do instrutor (`vitima.triagem`), o vínculo com a
 * ficha, objetos ocultos, anotações, o histórico e qualquer achado que ainda
 * não foi liberado. O relógio vai à parte (ver `canalProjecaoTabletop`), para
 * a cena não ser retransmitida a cada segundo.
 *
 * A projeção roda na mesma origem e com o mesmo bundle, então isto protege a
 * TELA (o que a turma vê no projetor), não o código contra quem abre o
 * DevTools — o que basta para a sala de aula.
 */
export interface EstadoPublicoTabletop {
  readonly versao: 1
  readonly idSessao: string
  readonly nomeCena: string
  readonly fase: FaseAtendimento
  /** mensagem do COBOM já montada — só os itens que o instrutor deixou visíveis */
  readonly acionamento: string
  readonly marcos: readonly MarcoOcorrencia[]
  /**
   * Sangramento visível na cena, por objeto de vítima (identificada ou não):
   * é o que qualquer um veria ao chegar. Some quando o problema é controlado.
   */
  readonly sangramentos: Readonly<Record<string, 'ativo' | 'parcial'>>
  /** aparência dos riscos (varrido, serragem, área isolada, energizado) — o que se vê, sem regra */
  readonly riscos: Readonly<Record<string, AparenciaRisco>>
  /** Objetos visíveis da cena, já sem os campos privados. */
  readonly objetos: readonly ObjetoTabletop[]
  /** Só as vítimas que a guarnição já encontrou, na ordem da cena. */
  readonly vitimas: readonly VitimaPublica[]
}

export interface LeituraPublica {
  readonly sigla: string
  readonly rotulo: string
  /** `null` enquanto o instrutor não mostrar à turma (ou quando a ficha não informa o dado). */
  readonly valor: string | null
}

export interface VitimaPublica {
  readonly id: string
  readonly nome: string
  readonly papel: string | null
  /** Classificação que a EQUIPE declarou — nunca a de referência do instrutor. */
  readonly triagemDeclarada: PrioridadeTriagem | null
  readonly colarColocado: boolean
  readonly leituras: readonly LeituraPublica[]
}

/**
 * `triagem` é obrigatória no tipo do bloco de vítima, mas o valor real é o
 * gabarito do instrutor. No estado público ele é sempre substituído por este
 * valor fixo, que não carrega significado: a projeção desenha a cor a partir
 * de `VitimaPublica.triagemDeclarada` (modo aluno), nunca deste campo.
 */
const TRIAGEM_SEM_SIGNIFICADO: PrioridadeTriagem = 'verde'
const NOME_VITIMA_NAO_IDENTIFICADA = 'Vítima'

export type ResolverFicha = (idObjetoVitima: string) => FichaVitimaAtendimento | null

/** Vítima conta como identificada quando o instrutor a marcou. */
export function vitimaEstaIdentificada(sessao: SessaoAtendimentoTabletop, idVitima: string): boolean {
  return sessao.vitimasIdentificadas.includes(idVitima)
}

function blocoVitimaPublico(vitima: BlocoVitimaTabletop): BlocoVitimaTabletop {
  return {
    idPerfil: vitima.idPerfil,
    local: vitima.local,
    triagem: TRIAGEM_SEM_SIGNIFICADO,
    // o colar que a turma vê vem das intervenções registradas na sessão
    colarCervical: false,
  }
}

function objetoPublico(objeto: ObjetoTabletop, identificada: boolean): ObjetoTabletop {
  return {
    id: objeto.id,
    tipo: objeto.tipo,
    nome: objeto.vitima && !identificada ? NOME_VITIMA_NAO_IDENTIFICADA : objeto.nome,
    transformacao: objeto.transformacao,
    ...(objeto.cor !== undefined ? { cor: objeto.cor } : {}),
    ...(objeto.veiculo ? { veiculo: { ...objeto.veiculo, pecaSelecionada: null } } : {}),
    ...(objeto.equipamento ? { equipamento: objeto.equipamento } : {}),
    ...(objeto.vitima ? { vitima: blocoVitimaPublico(objeto.vitima) } : {}),
  }
}

/**
 * Só a LEITURA REGISTRADA mais recente de cada exame, e só se o instrutor a
 * liberou ("mostrar à turma"). Nunca o estado atual da ficha: se a vítima
 * mudou, a turma só vê depois de reavaliar.
 */
function leiturasLiberadas(sessao: SessaoAtendimentoTabletop, idVitima: string): LeituraPublica[] {
  const estado = sessao.estadosVitimas[idVitima]
  return SLOTS_MONITOR.map((slot) => {
    const leitura = estado && estado.liberadasNaTela.includes(slot.idAcao) ? ultimaLeitura(estado, slot.idAcao) : null
    if (!leitura || leitura.achado.estado === 'nao_informado') {
      return { sigla: slot.sigla, rotulo: slot.rotulo, valor: null }
    }
    return { sigla: slot.sigla, rotulo: slot.rotulo, valor: leituraCurta(leitura.achado) }
  })
}

export function montarEstadoPublico(
  cena: CenaTabletop,
  sessao: SessaoAtendimentoTabletop,
  resolverFicha: ResolverFicha,
): EstadoPublicoTabletop {
  const ocultos = new Set(cena.objetos.filter((o) => o.oculto).map((o) => o.id))
  // vítima sentada num veículo oculto também não aparece: ela é desenhada
  // dentro do grupo do carro, que não vai para a projeção
  const visivel = (objeto: ObjetoTabletop) => {
    if (ocultos.has(objeto.id)) return false
    const local = objeto.vitima?.local
    return !(local?.tipo === 'veiculo' && ocultos.has(local.idVeiculo))
  }

  const objetos: ObjetoTabletop[] = []
  const vitimas: VitimaPublica[] = []
  const sangramentos: Record<string, 'ativo' | 'parcial'> = {}

  for (const objeto of cena.objetos) {
    if (!visivel(objeto)) continue
    const identificada = objeto.vitima ? vitimaEstaIdentificada(sessao, objeto.id) : false
    objetos.push(objetoPublico(objeto, identificada))
    if (!objeto.vitima) continue

    const ficha = resolverFicha(objeto.id)
    const estado = sessao.estadosVitimas[objeto.id]
    const sangramento = ficha ? problemasVisiveisAtivos(ficha, estado ?? ESTADO_VITIMA_VAZIO) : null
    if (sangramento) sangramentos[objeto.id] = sangramento

    if (!identificada) continue
    vitimas.push({
      id: objeto.id,
      nome: objeto.nome,
      papel: ficha?.perfil.papel ?? null,
      triagemDeclarada: estado?.triagemDeclarada ?? null,
      colarColocado: estado?.intervencoesRealizadas.some((i) => i.tipo === 'colar_cervical') ?? false,
      leituras: leiturasLiberadas(sessao, objeto.id),
    })
  }

  return {
    versao: 1,
    idSessao: sessao.id,
    nomeCena: cena.nome,
    fase: sessao.fase,
    acionamento: mensagemAcionamento(acionamentoDaCena(cena)),
    marcos: sessao.marcos,
    sangramentos,
    riscos: aparenciasDosRiscos(cena, sessao),
    objetos,
    vitimas,
  }
}
